import {Component, inject, OnDestroy, OnInit} from "@angular/core";
import {Store} from "@ngrx/store";
import {selectFundSellerPosition, selectIsAnyFundFormInEditMode, selectIsFundSizeZero, selectRelatedDealId} from "../../store/fund/fund.selectors";
import {FormBuilder, Validators} from "@angular/forms";
import {numberNotBiggerThan190000m} from "../../../shared/utils/form-validators";
import {FundSellerPositionActions} from "../../store/fund.actions";
import {map, Subscription} from "rxjs";
import {DecimalFormatPipe} from "../../../shared/pipes/decimal-format/decimal-format.pipe";
import {CalculationUtil} from "../../../shared/utils/calculation-util";
import Big from "big.js";
import {SellerPositionActions} from "../../../deal/store/deal.actions";
import {selectSelectedFundValuationId} from "../../store/fund-valuation/fund-valuation.selectors";
import {EMPTY_SELLER_POSITION, SellerPosition} from "../../../deal/models/seller-position";
import {MatDialog} from "@angular/material/dialog";
import {ConfirmationComponent} from "../../../shared/components/confirmation/confirmation.component";

const SOURCE_USER = "user";
const SOURCE_CALCULATED = "calculated";

@Component({
    selector: "valumize-fund-seller-position",
    templateUrl: "./fund-seller-position.component.html",
    standalone: false
})
export class FundSellerPositionComponent implements OnInit, OnDestroy {
    private readonly store = inject(Store);
    private readonly formBuilder = inject(FormBuilder);
    private readonly dialog = inject(MatDialog);

    subscriptions: Subscription[] = [];

    dealId$ = this.store.select(selectRelatedDealId);
    fundValuationId$ = this.store.select(selectSelectedFundValuationId);
    fundSellerPosition$ = this.store.select(selectFundSellerPosition);
    isEditDisabled$ = this.store.select(selectIsAnyFundFormInEditMode);
    isFundSizeZero$ = this.store.select(selectIsFundSizeZero);

    persistedSellerPosition = EMPTY_SELLER_POSITION;
    isEditable = false;

    constructor( ) {
    }

    sellerPositionForm = this.formBuilder.group({
        originalCommitment: this.formBuilder.control<number | null>(null, {nonNullable: true, validators: [Validators.required, numberNotBiggerThan190000m()]}),
        recallableDistribution: this.formBuilder.control<number | null>(null, {validators: [numberNotBiggerThan190000m()]}),
        drawnCommitments: this.formBuilder.control<number | null>(null, {validators: [numberNotBiggerThan190000m()]}),
        distributions: this.formBuilder.control<number | null>(null, {validators: [numberNotBiggerThan190000m()]}),
        gpValuation: this.formBuilder.control<number | null>(null, {validators: [numberNotBiggerThan190000m()]}),
        findersFee: this.formBuilder.control<number | null>(null, {validators: [numberNotBiggerThan190000m()]}),
        postRefDistributions: this.formBuilder.control<number | null>(null, {validators: [numberNotBiggerThan190000m()]}),
        postRefDrawdowns: this.formBuilder.control<number | null>(null, {validators: [numberNotBiggerThan190000m()]})
    });

    ngOnInit() {
        this.subscriptions.push(
            this.store.select(selectSelectedFundValuationId).pipe(map((fundValuationId) => {
                if (fundValuationId) {
                    this.store.dispatch(SellerPositionActions.loadDataset({fundValuationId}));
                }
            })).subscribe());

        this.subscriptions.push(
            this.store.select(selectFundSellerPosition).pipe(map((fundSellerPosition => {
                this.isEditable = fundSellerPosition.fundSellerPositionIsEditable;
                this.persistedSellerPosition = fundSellerPosition.sellerPosition.data;

                this.sellerPositionForm.patchValue({
                    originalCommitment: DecimalFormatPipe.transformFromMillionsNum(fundSellerPosition.sellerPosition.data.sellerCommitment.amount ?? undefined),
                    recallableDistribution: DecimalFormatPipe.transformFromMillionsNum(fundSellerPosition.sellerPosition.data.recallableDistribution.amount ?? undefined),
                    drawnCommitments: DecimalFormatPipe.transformFromMillionsNum(fundSellerPosition.sellerPosition.data.drawnCommitments.amount ?? undefined),
                    distributions: DecimalFormatPipe.transformFromMillionsNum(fundSellerPosition.sellerPosition.data.distributions.amount ?? undefined),
                    gpValuation: DecimalFormatPipe.transformFromMillionsNum(fundSellerPosition.sellerPosition.data.gpValuation.amount ?? undefined),
                    findersFee: DecimalFormatPipe.transformFromMillionsNum(fundSellerPosition.sellerPosition.data.findersFee.amount ?? undefined),
                    postRefDistributions: DecimalFormatPipe.transformFromMillionsNum(fundSellerPosition.sellerPosition.data.postRefDistributions.amount ?? undefined),
                    postRefDrawdowns: DecimalFormatPipe.transformFromMillionsNum(fundSellerPosition.sellerPosition.data.postRefDrawdowns.amount ?? undefined)
                });
            }))).subscribe());
    }

    save(dealId: number, fundValuationId: number) {
        if (this.sellerPositionForm.valid) {
            if (this.isUndrawnNegative) {
                const dialogRef = this.dialog.open(ConfirmationComponent);
                dialogRef.componentInstance.confirmMessage = "Save with negative total undrawn?";
                dialogRef.componentInstance.htmlContent =
                    "The calculated total undrawn for this seller position is negative (drawn commitments exceed seller commitment). "
                    + "Please confirm this is intentional before saving.";
                dialogRef.afterClosed().pipe(map(confirmed => {
                    if (confirmed) {
                        this.dispatchSave(dealId, fundValuationId);
                    }
                })).subscribe();
            } else {
                this.dispatchSave(dealId, fundValuationId);
            }
        }
    }

    private dispatchSave(dealId: number, fundValuationId: number) {
        const form = this.sellerPositionForm.getRawValue();
        // The backend stamps source=user / userId / modDate on the fields the user actually changed,
        // so here we only carry the edited amounts; unchanged fields pass through untouched.
        const sellerPositionToSave: SellerPosition = {
            ...this.persistedSellerPosition,
            sellerCommitment: {
                ...this.persistedSellerPosition.sellerCommitment,
                amount: DecimalFormatPipe.transformToMillionsNum(form.originalCommitment ?? undefined)
            },
            recallableDistribution: {
                ...this.persistedSellerPosition.recallableDistribution,
                amount: DecimalFormatPipe.transformToMillionsNum(form.recallableDistribution ?? undefined)
            },
            drawnCommitments: {
                ...this.persistedSellerPosition.drawnCommitments,
                amount: DecimalFormatPipe.transformToMillionsNum(form.drawnCommitments ?? undefined)
            },
            distributions: {
                ...this.persistedSellerPosition.distributions,
                amount: DecimalFormatPipe.transformToMillionsNum(form.distributions ?? undefined)
            },
            gpValuation: {
                ...this.persistedSellerPosition.gpValuation,
                amount: DecimalFormatPipe.transformToMillionsNum(form.gpValuation ?? undefined)
            }
        };
        this.store.dispatch(SellerPositionActions.save({dealId, fundValuationId, sellerPosition: sellerPositionToSave}));
    }

    private get isUndrawnNegative(): boolean {
        const form = this.sellerPositionForm.getRawValue();
        const sp = this.persistedSellerPosition;

        // All terms are in raw (full) units and the arithmetic runs through Big.js per the
        // project convention to stay decimal-exact on these BigDecimal-derived amounts.

        // originalCommitment is required — always set in form.
        const commitment = CalculationUtil.toBig(DecimalFormatPipe.transformToMillionsNum(form.originalCommitment ?? undefined)) ?? new Big(0);

        // drawnCommitments: form value = user override; null means backend will use the calculated value.
        const drawn = (form.drawnCommitments != null
            ? CalculationUtil.toBig(DecimalFormatPipe.transformToMillionsNum(form.drawnCommitments))
            : CalculationUtil.toBig(sp.drawnCommitmentsCalculated?.amount ?? sp.drawnCommitments.amount)) ?? new Big(0);

        // recallableDistribution: same override logic.
        const recallable = (form.recallableDistribution != null
            ? CalculationUtil.toBig(DecimalFormatPipe.transformToMillionsNum(form.recallableDistribution))
            : CalculationUtil.toBig(sp.recallableDistributionCalculated?.amount ?? sp.recallableDistribution.amount)) ?? new Big(0);

        return commitment.minus(drawn).plus(recallable).lt(0);
    }

    resetToCalculated(dealId: number, fundValuationId: number) {
        const sp = this.persistedSellerPosition;
        const sellerPositionToSave: SellerPosition = {
            ...sp,
            drawnCommitments: {source: SOURCE_CALCULATED, modDate: new Date().toISOString(), amount: sp.drawnCommitmentsCalculated?.amount},
            distributions: {source: SOURCE_CALCULATED, modDate: new Date().toISOString(), amount: sp.distributionsCalculated?.amount},
            gpValuation: {source: SOURCE_CALCULATED, modDate: new Date().toISOString(), amount: sp.gpValuationCalculated?.amount},
            recallableDistribution: {source: SOURCE_CALCULATED, modDate: new Date().toISOString(), amount: sp.recallableDistributionCalculated?.amount}
        };
        this.store.dispatch(SellerPositionActions.save({dealId, fundValuationId, sellerPosition: sellerPositionToSave}));
    }

    get hasOverride(): boolean {
        const sp = this.persistedSellerPosition;
        return sp.drawnCommitments.source === SOURCE_USER
            || sp.distributions.source === SOURCE_USER
            || sp.gpValuation.source === SOURCE_USER
            || sp.recallableDistribution.source === SOURCE_USER;
    }

    get documentContext(): string | null {
        const id = this.persistedSellerPosition?.id;
        return id != null ? `/portfolio/seller-positions/${id}/capital-account-statement/` : null;
    }

    editMode = () => this.store.dispatch(FundSellerPositionActions.edit());

    cancel = () => this.store.dispatch(FundSellerPositionActions.cancel());

    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }
}
